import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { matchRoutes } from 'react-router-config';
import loadable from '@loadable/component';
import * as R from 'ramda';
import classNames from 'classnames';
import Container from 'reactstrap/lib/Container';

import { App as AppCore } from 'core/app';
import {
  withCoreComponent, withLocale, withUser, withModalActions, withImagesData, withUserAgent
} from 'core/hocs';
import {
  isEmptyOrNil, wrapRoute2Locale, getEnv, convertStringifiedBoolean
} from 'core/helpers';
import { DEVICES_TYPES, USER_FIELDS } from 'core/constants';

import { withPwaInstall, withPwaInstallContext } from 'hocs/with-pwa-install';
import { IconsDefs } from 'components/icons/icons-defs';
import { Header } from 'components/header/header';
import { AuthModal } from 'components/modals/auth-modal/auth-modal';
import { SupportModal } from 'components/modals/support-modal/support-modal';
import { LinkErrorModal } from 'components/modals/link-error-modal/link-error-modal';
import { DepositModal } from 'components/modals/deposit-modal/deposit-modal';
import { NewLevelModal } from 'components/modals/new-level-modal/new-level-modal';
import { CompleteSignUpModal } from 'components/modals/complete-sign-up-modal/complete-sign-up-modal';
import { ResetPasswordModal } from 'components/modals/reset-password-modal/reset-password-modal';
import { GlobalNotificationsStack } from 'components/global-notifications-stack/global-notifications-stack';
import { Sidebar } from 'components/sidebar/sidebar';
import { Footer } from 'components/footer/footer';
import { CasinoNavigation } from 'components/casino-navigation/casino-navigation';
import { RecaptchaLoader } from 'components/recaptcha-loader/recaptcha-loader';
import { GlobalEventsHandler } from 'components/global-events-handler/global-events-handler';
import { SkipServerRender } from 'components/skip-server-render/skip-server-render';
import { LocationTracker } from 'components/location-tracker/location-tracker';
import { ConfirmOtpModal } from 'components/modals/confirm-otp-modal/confirm-otp-modal';
import { SetupOtpModal } from 'components/modals/setup-otp-modal/setup-otp-modal';
import { SetupSms2faModal } from 'components/modals/setup-sms-2fa-modal/setup-sms-2fa-modal';
import { TooManyAttempts2faModal } from 'components/modals/too-many-attempts-2fa-modal/too-many-attempts-2fa-modal';
import { RemoveOtpModal } from 'components/modals/remove-otp-modal/remove-otp-modal';
import { CookiesPolicy } from 'components/cookies-policy/cookies-policy';
import { MobileGameContainer } from 'components/mobile-game-container/mobile-game-container';
import { PwaPaymentContainer } from 'components/pwa-payment-container/pwa-payment-container';
// import { PwaInstallGuide } from 'components/pwa-install-guide/pwa-install-guide';
import { PwaInstallModals } from 'components/pwa-install-modals/pwa-install-modals';
import { SignInModal } from 'components/modals/sign-in-modal/sign-in-modal';
import { SignUpModal } from 'components/modals/sign-up-modal/sign-up-modal';
import { StoriesModal } from 'components/stories/stories-modal/stories-modal';
import { PromoInfoModal } from 'src/components/modals/promo-info-modal/promo-info-modal';
import { SocialAuthTracker } from 'components/social-auth-tracker/social-auth-tracker';
import { FreeSpinsNotifier } from 'components/free-spins-notifier/free-spins-notifier';
import { ConfirmPhoneModal } from 'components/modals/confirm-phone-modal/confirm-phone-modal';
import { ConfirmSms2faModal } from 'components/modals/confirm-sms-2fa-modal/confirm-sms-2fa-modal';
import { SideModalBar } from 'components/side-modal-bar/side-modal-bar';
import { ProvidePhoneModal } from 'components/modals/provide-phone-modal/provide-phone-modal';
import { CioTracker } from 'components/trackers/cio-tracker';
import { PaymentCompletionModal } from 'components/modals/payment-completion-modal/payment-completion-modal';
import { LiveChatIncChat } from './components/live-chats/live-chat-inc-chat/live-chat-inc-chat';
import { GameWinnerCarousel } from './components/game-winner-list/game-winner-carousel';

import {
  MODAL_IDS,
  AUTH_TYPES,
  PAGE_NAMES,
  LIVECHAT_INC_GROUPS,
  IMAGES_DATA_CATEGORIES,
} from './constants';

import './locales';

import './app.scss';

const {
  LOYALTY,
  GAME,
  TOURNAMENT,
  RESET_PASSWORD,
  HOME,
  WALLET,
  TOURNAMENTS,
  GAMES,
  PROVIDER,
  CATEGORY,
  PAYMENTS,
  BANKING_HISTORY,
  SPORTSBOOK,
  IN_PLAY,
  FAQ,
  SIGN_UP,
  LOGIN_STANDALONE,
  PROFILE,
  PLAYER_PROMO,
  CASHIER,
  LIMITS,
  UPDATE_PASSWORD,
  FAVORITES,
  EXCHANGE_CP,
} = PAGE_NAMES;

export const PROFILE_PAGES = [
  PROFILE,
  PLAYER_PROMO,
  CASHIER,
  LIMITS,
  UPDATE_PASSWORD,
  FAVORITES,
  EXCHANGE_CP
];

const PageNotFound = loadable(() => import(/* webpackChunkName: "page-not-found" */ './pages/page-not-found/page-not-found'));

const PAGES_WITHOUT_AUTH_MODAL_AUTO_SHOW = [SIGN_UP, RESET_PASSWORD, LOGIN_STANDALONE];
const { LOGGED_IN_MODAL_BANNER } = IMAGES_DATA_CATEGORIES;

const STANDALONE_SIGN_IN = convertStringifiedBoolean(getEnv('STANDALONE_SIGN_IN'));

const AUTH_MODAL_TIMEOUT = {
  SHORT: 30000, // 30 sec
  LONG: 120000 // 2 min
};

export class AppUI extends Component {
  static propTypes = {
    renderRoutes: PropTypes.func.isRequired,
    route: PropTypes.shape({
      routes: PropTypes.arrayOf(
        PropTypes.shape({
          path: PropTypes.oneOfType([PropTypes.string, PropTypes.arrayOf(PropTypes.string)]),
          exact: PropTypes.bool,
        }),
      ),
    }).isRequired,
    is404: PropTypes.bool.isRequired,
    deviceType: PropTypes.string.isRequired,
    history: PropTypes.shape().isRequired,
    imagesData: PropTypes.shape().isRequired,
    locale: PropTypes.string.isRequired,
    isPwa: PropTypes.bool.isRequired,
    openModal: PropTypes.func.isRequired,
    isUserLoggedIn: PropTypes.bool.isRequired,
    userData: PropTypes.shape().isRequired,
    isUserRegistered: PropTypes.bool.isRequired,
    getCurrentModalOpened: PropTypes.func.isRequired,
    openModalAndCloseOthers: PropTypes.func.isRequired,
    os: PropTypes.string.isRequired,
  };

  static contextType = withPwaInstallContext

  state = {
    authModalShowCount: 0,
  };

  componentDidMount() {
    const {
      route: { routes },
      history,
    } = this.props;

    const [routeInfo] = matchRoutes(routes, history.location.pathname);
    const currentRoute = R.propOr({}, 'route', routeInfo);

    if (!STANDALONE_SIGN_IN && !PAGES_WITHOUT_AUTH_MODAL_AUTO_SHOW.includes(currentRoute.page)) {
      this.authModalTimerID = setTimeout(() => {
        this.showAuthModal();
      }, AUTH_MODAL_TIMEOUT.LONG);
    }
  }

  componentDidUpdate(prevProps) {
    const {
      isUserLoggedIn, openModal, imagesData, history
    } = this.props;
    const { isUserLoggedIn: isUserLoggedInPrev } = prevProps;

    const isBonusExist = !isEmptyOrNil(imagesData[LOGGED_IN_MODAL_BANNER]);

    if (!isUserLoggedInPrev && isUserLoggedIn && isBonusExist) {
      openModal(MODAL_IDS.PROMO_INFO);
    }

    if (STANDALONE_SIGN_IN && !history.location.pathname.includes('/login')) {
      history.replace(wrapRoute2Locale('/login'));
    }
  }

  showAuthModal = () => {
    const {
      isUserRegistered,
      isUserLoggedIn,
      getCurrentModalOpened,
      openModalAndCloseOthers,
    } = this.props;

    const [isSignInModalOpened, isSignUpModalOpened, isAuthModalOpen] = [
      getCurrentModalOpened(MODAL_IDS.SIGN_IN),
      getCurrentModalOpened(MODAL_IDS.SIGN_UP),
      getCurrentModalOpened(MODAL_IDS.AUTH)
    ];

    if (isSignInModalOpened || isSignUpModalOpened) {
      this.clearAuthModalTimer();

      return;
    }

    if (!isAuthModalOpen && !isUserLoggedIn) {
      const authModalType = isUserRegistered
        ? AUTH_TYPES.SIGN_IN
        : AUTH_TYPES.SIGN_UP;

      this.setState(prevState => ({
        authModalShowCount: prevState.authModalShowCount + 1
      }));

      openModalAndCloseOthers({
        id: MODAL_IDS.AUTH,
        data: authModalType,
      });

      if (this.state.authModalShowCount < 3) {
        this.authModalTimerID = setTimeout(() => {
          this.showAuthModal();
        }, AUTH_MODAL_TIMEOUT.LONG);
      } else {
        this.clearAuthModalTimer();
      }
    }
  };

  clearAuthModalTimer = () => {
    if (this.authModalTimerID) {
      clearTimeout(this.authModalTimerID);
    }
  };

  render() {
    const {
      renderRoutes,
      route: { routes },
      is404,
      history,
      locale,
      deviceType,
      isPwa,
      userData,
      isUserRegistered,
      isUserLoggedIn,
      os,
    } = this.props;

    const [routeInfo] = matchRoutes(routes, history.location.pathname);
    const currentRoute = R.propOr({}, 'route', routeInfo);

    const {
      page: pageName,
      noSidebar: hideSidebar,
      noHeader: hideHeader,
      noChat: hideChat,
      noFooter,
      hasNavigation,
      mobileBackRoute,
    } = currentRoute;

    // const { isDomainBarOpen } = this.context;

    const routeParams = R.pathOr({}, ['match', 'params'], routeInfo);
    const isLoyaltyPage = pageName === LOYALTY;
    const isGamePage = pageName === GAME;
    const isProfilePage = PROFILE_PAGES.includes(pageName);
    const isGamesPage = pageName === GAMES;
    const isHomePage = pageName === HOME;
    const isTournamentPage = pageName === TOURNAMENT;
    const isTournamentsPage = pageName === TOURNAMENTS;
    const isResetPasswordPage = pageName === RESET_PASSWORD;
    const isWalletPage = pageName === WALLET;
    const isProviderPage = pageName === PROVIDER;
    const isCategoryPage = pageName === CATEGORY;
    const isPaymentsPage = pageName === PAYMENTS;
    const isSportsbookPage = [SPORTSBOOK, IN_PLAY].includes(pageName);
    const isFaqPage = pageName === FAQ;
    const isBankingHistoryPage = pageName === BANKING_HISTORY;
    // const isLoginPage = pageName === LOGIN_STANDALONE;
    const liveChatGroup = LIVECHAT_INC_GROUPS[locale];

    const isMobile = deviceType === DEVICES_TYPES.MOBILE;
    const isGamePageOnMobile = isMobile && isGamePage;
    const isAndroid = os === 'android';

    if (is404) {
      return (<PageNotFound />);
    }

    return (
      <>
        <GlobalNotificationsStack />

        {/* for correct opening mobile game in iframe by direct link */}
        {/* <MobileGameContainer /> should mount before <Game /> */}
        {(isPwa || isGamePageOnMobile) && (
          <MobileGameContainer
            isPwa={isPwa}
            isMobile={isMobile}
            isGamePage={isGamePageOnMobile}
          />
        )}

        <div>
          {(!hideSidebar && !isGamePageOnMobile) && <Sidebar pageName={pageName} mobileBackRoute={mobileBackRoute} />}

          {/* <SkipServerRender skip> */}
          {/*  {!isPwa */}
          {/*    && !isMobile */}
          {/*    && !isSportsbookPage */}
          {/*    && !isGamePage */}
          {/*    && !isResetPasswordPage */}
          {/*    && !isLoginPage */}
          {/*    && ( */}
          {/*      <PwaInstallGuide /> */}
          {/*    )} */}
          {/* </SkipServerRender> */}

          {!hideHeader && (
            <Header
              pageName={pageName}
              mobileBackRoute={mobileBackRoute}
              params={routeParams.category}
              className={classNames({
                'main-content-overlap': [HOME, GAMES].includes(pageName),
                'position-absolute casino-header-overlay': isHomePage || isGamesPage,
              })}
            />
          )}

          {/* <SkipServerRender skip> */}
          {/*  {!isPwa */}
          {/*    && isMobile */}
          {/*    && !isSportsbookPage */}
          {/*    && !isGamePage */}
          {/*    && !isResetPasswordPage */}
          {/*    && !isLoginPage */}
          {/*    && ( */}
          {/*      <PwaInstallGuide /> */}
          {/*    )} */}
          {/* </SkipServerRender> */}

          <Container
            fluid
            className={classNames('d-flex flex-column main-container position-relative flex-grow-1', {
              'is-main-page': !isLoyaltyPage && !isGamePage && !isHomePage && !isTournamentPage && !isGamesPage,
              'px-sm-2_5 px-lg-5': !isLoyaltyPage && !isGamePage && !isTournamentPage && !isHomePage && !isGamesPage && !isWalletPage && !isBankingHistoryPage && !isSportsbookPage,
              'px-0': isLoyaltyPage || isGamePage || isTournamentPage || isHomePage || isWalletPage || isGamesPage || isSportsbookPage,
              'hide-sidebar': hideSidebar || isGamePageOnMobile,
              'has-navigation': hasNavigation && noFooter,
              'has-content-under-header': isHomePage || isTournamentPage || isGamesPage,
              'with-header': !hideHeader,
              'px-sm-5': isWalletPage || isBankingHistoryPage,
            })}
          >
            <SkipServerRender skip>
              {!STANDALONE_SIGN_IN
                && !isGamePage
                && !isProfilePage
                && !isSportsbookPage
                && <GameWinnerCarousel locale={locale} withStubs />}
            </SkipServerRender>

            {renderRoutes(routes)}
          </Container>
        </div>

        {!noFooter && (
          <Footer className={classNames({
            'mt-6': !isLoyaltyPage && !isTournamentsPage && !isGamesPage && !isCategoryPage && !isProviderPage && !isPaymentsPage && !isGamePage && !isFaqPage,
            'has-navigation': hasNavigation,
            'pt-3_5': isLoyaltyPage,
            'px-2 px-sm-2_5 px-lg-5': !isResetPasswordPage,
            'reset-password-page-footer mx-auto px-2_5 d-none d-sm-block': isResetPasswordPage,
            'hide-sidebar': hideSidebar,
            'mt-5': isProviderPage || isCategoryPage || isPaymentsPage,
          })}
          />
        )}

        <SkipServerRender skip>
          <SocialAuthTracker />
          <SignInModal />
          <SignUpModal />
          <StoriesModal />
          <PromoInfoModal />
          <SupportModal />
          {isHomePage && <PwaInstallModals />}

          <SideModalBar
            pageName={pageName}
            params={routeParams.category}
            isSportsbookPage={isSportsbookPage}
          />

          <ProvidePhoneModal />
          <CompleteSignUpModal />
          <ResetPasswordModal />
          <LinkErrorModal />
          <DepositModal />
          <NewLevelModal />
          <FreeSpinsNotifier />
          <ConfirmPhoneModal />
          <ConfirmSms2faModal />
          <PaymentCompletionModal />

          {!STANDALONE_SIGN_IN && <AuthModal />}

          <ConfirmOtpModal />
          <SetupOtpModal />
          <SetupSms2faModal />
          <TooManyAttempts2faModal />
          <RemoveOtpModal />

          {hasNavigation && (
            <CasinoNavigation
              pageName={pageName}
              params={routeParams.category}
            />
          )}

          {!hideChat && <LiveChatIncChat group={liveChatGroup} isButtonHidden={isSportsbookPage} />}

          <RecaptchaLoader />

          <LocationTracker pageName={pageName} />

          {!STANDALONE_SIGN_IN && !isPwa && <CookiesPolicy hasNavigation={hasNavigation} noSidebar={hideSidebar} />}
          {(isPwa || isAndroid) && <PwaPaymentContainer />}

          <CioTracker
            userData={userData}
            isUserLoggedIn={isUserLoggedIn}
            isUserRegistered={isUserRegistered}
          />
        </SkipServerRender>

        <GlobalEventsHandler pageName={pageName} />
        <IconsDefs />
      </>
    );
  }
}

export const App = withCoreComponent(
  AppCore,
  withUserAgent(
    withLocale(
      withPwaInstall(
        withModalActions(
          withImagesData(
            withUser(AppUI, [
              USER_FIELDS.IS_USER_LOGGED_IN,
              USER_FIELDS.IS_USER_REGISTERED,
              USER_FIELDS.USER_DATA
            ])
          )
        )
      )
    )
  )
);
